import React, { useEffect, useState } from 'react';
import { MessageSquare, MailOpen, Archive, Mail, ChevronDown, ChevronUp, RefreshCw, Loader2 } from 'lucide-react';
import { adminListFeedback, adminSetFeedbackStatus } from '../services/api';

const STATUS_TABS = ['new', 'read', 'archived', 'all'];
const CATEGORY_TONES = {
  bug: 'bg-rose-50 text-rose-700 border-rose-200',
  idea: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  missing: 'bg-amber-50 text-amber-800 border-amber-200',
  general: 'bg-slate-100 text-slate-700 border-slate-200',
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
};

const FeedbackPanel = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('new');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListFeedback(token);
      setItems(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const counts = STATUS_TABS.reduce((acc, t) => {
    acc[t] = t === 'all' ? items.length : items.filter((x) => x.status === t).length;
    return acc;
  }, {});

  const visible = items.filter((x) => (tab === 'all' ? true : x.status === tab));

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await adminSetFeedbackStatus(token, id, status);
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 mb-6 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
            <MessageSquare size={16} />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-slate-900 text-[15px]">Feedback inbox</h3>
            <p className="text-[12.5px] text-slate-500">
              User suggestions, bug reports and missing startups.
            </p>
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2 mb-3">
            {STATUS_TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`text-[12px] font-medium px-2.5 py-1 rounded-md transition-colors ${
                  tab === t ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {t[0].toUpperCase() + t.slice(1)}
                <span className={`ml-1 ${tab === t ? 'text-slate-300' : 'text-slate-400'}`}>
                  {counts[t] || 0}
                </span>
              </button>
            ))}
            <button
              onClick={load}
              className="ml-auto text-slate-500 hover:text-slate-900 p-1.5 rounded-md hover:bg-slate-100"
              title="Refresh"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {loading && !items.length ? (
            <p className="text-center text-[13px] text-slate-500 py-6">Loading…</p>
          ) : !visible.length ? (
            <p className="text-center text-[13px] text-slate-500 py-6">No {tab !== 'all' ? tab : ''} feedback.</p>
          ) : (
            <ul className="space-y-2">
              {visible.map((f) => (
                <li key={f.id} className="border border-slate-100 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-[10.5px] font-medium px-1.5 py-0.5 rounded-full border uppercase tracking-wide ${CATEGORY_TONES[f.category] || CATEGORY_TONES.general}`}>
                          {f.category}
                        </span>
                        <span className="text-[10.5px] font-medium px-1.5 py-0.5 rounded-full border border-slate-200 text-slate-500 uppercase tracking-wide">
                          {f.status}
                        </span>
                        <span className="text-[11.5px] text-slate-400">{fmtWhen(f.submittedAt)}</span>
                      </div>
                      <p className="text-[13px] text-slate-800 whitespace-pre-wrap leading-snug">{f.message}</p>
                      <div className="mt-1.5 flex items-center gap-2 text-[11.5px] text-slate-500">
                        {f.email ? (
                          <a href={`mailto:${f.email}`} className="hover:text-slate-900 inline-flex items-center gap-1">
                            <Mail size={11} /> {f.email}
                          </a>
                        ) : (
                          <span className="italic">anonymous</span>
                        )}
                        {f.page && <span className="text-slate-400">· from {f.page}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {f.status !== 'read' && (
                        <button
                          onClick={() => setStatus(f.id, 'read')}
                          disabled={busyId === f.id}
                          title="Mark as read"
                          className="p-1.5 rounded-md text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                        >
                          {busyId === f.id ? <Loader2 size={12} className="animate-spin" /> : <MailOpen size={12} />}
                        </button>
                      )}
                      {f.status !== 'archived' && (
                        <button
                          onClick={() => setStatus(f.id, 'archived')}
                          disabled={busyId === f.id}
                          title="Archive"
                          className="p-1.5 rounded-md text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                        >
                          <Archive size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackPanel;
